/**
 * Typed query/mutation meta for the layered tRPC error-handling model.
 * See docs/guidelines/trpc-error-handling.md.
 *
 * The `Register` module augmentation gives every `useQuery` / `useMutation`
 * call site type-safe `meta` so the global `QueryCache.onError` /
 * `MutationCache.onError` handlers in `client/src/trpc/client.tsx` can read
 * `errorSurface` to decide who owns Sentry capture and whether to pop a modal.
 */

/**
 * Where a query/mutation failure should surface.
 *
 * - `silent`   — background poll / prefetch. Cache handler captures to Sentry; no UI.
 * - `inline`   — component renders `InlineQueryError`. Cache handler captures to Sentry; no modal.
 * - `modal`    — global handler shows the error modal. Cache handler captures to Sentry.
 *                For mutations, this is mutually exclusive with a local `onError`.
 * - `route`    — query throws via `throwOnError`; TanStack Router's `errorComponent`
 *                renders and owns Sentry capture. Cache handler skips capture.
 * - `boundary` — query throws via `throwOnError`; the nearest `QueryErrorBoundary`
 *                (a `Sentry.ErrorBoundary` composed with `QueryErrorResetBoundary`)
 *                renders and owns Sentry capture. Cache handler skips capture.
 */
export type ErrorSurface = 'silent' | 'inline' | 'modal' | 'route' | 'boundary';

export type MutationErrorSurface = Extract<ErrorSurface, 'silent' | 'modal'>;

export interface CuriumQueryMeta extends Record<string, unknown> {
    /** Where a query failure should surface. Default: `'inline'`. */
    errorSurface?: ErrorSurface;
    /**
     * Override default Sentry capture for this query. Defaults to `true`
     * unless `errorSurface` is `'route'` or `'boundary'` (those have their
     * own Sentry owner).
     */
    captureToSentry?: boolean;
}

export interface CuriumMutationMeta {
    /**
     * Where a mutation failure should surface. Default: undefined (local
     * `onError` handles UI). `'modal'` is mutually exclusive with a local
     * `onError` callback — enforce via lint or the `useCuriumMutation`
     * wrapper. See docs/guidelines/trpc-error-handling.md.
     */
    errorSurface?: MutationErrorSurface;
    /** Override default Sentry capture for this mutation. Default `true`. */
    captureToSentry?: boolean;
}

/** Expected public readiness failures are rendered by their consumers and are not actionable telemetry. */
export const PUBLIC_READINESS_QUERY_META = {
    errorSurface: 'silent',
    captureToSentry: false,
} satisfies CuriumQueryMeta;

declare module '@tanstack/react-query' {
    interface Register {
        queryMeta: CuriumQueryMeta;
        mutationMeta: CuriumMutationMeta;
    }
}

/** Default surface used by the cache handler when meta is absent. */
export const DEFAULT_QUERY_ERROR_SURFACE: ErrorSurface = 'inline';

/** Surfaces whose Sentry capture is owned by a downstream boundary, not the cache. */
export const SURFACES_WITH_DOWNSTREAM_SENTRY_OWNER: ReadonlySet<ErrorSurface> = new Set(['route', 'boundary']);
